import connect from 'components/Shared/connect';
import { ConfiguratorProps, ModalProps } from 'views/ProductPage/configurators/index';
import {
  LawnMowerInstallationConfigurator,
  LawnMowerAddonProduct,
  IInstallationAddonProduct,
  VvsInstallationConfigurator,
  VvsInstallationProduct,
  IZipFetchInstallationProduct
} from 'models/InstallationConfigurator';
import {
  selectConfiguratorInstallation,
  selectConfiguratorAddon,
  resetConfiguratorAddons,
  resetInstallationConfigurator
} from 'views/ProductPage/configurators/installation/actions';
import {
  isInstallationSelected,
  isLawnMowerProduct,
  isVvsProduct,
  getProducts
} from 'views/ProductPage/configurators/installation/selector';
import React, { Reducer, useReducer, useEffect, useMemo, useState, ReactNode } from 'react';
import styles from './styles.scss';
import { rm, NO_ELEMENT } from 'utils';
import { ReactComponent } from 'models/ReactComponent';
import { postCheckInstallationZipCode } from 'services/ApiProductPageService';
import { handleServerError } from 'components/Shared/server';
import { IBasicResponse } from 'models/IBasicResponse';
import { LoadingStatus } from 'models/LoadingStatus';
import { getConfiguratorVariables } from 'views/ProductPage/selector';
import { onDeleteValue, onSelectValue } from 'views/ProductPage/configurators/actions';
import {
  LawnmowerAddon,
  LawnmowerAddonSection,
  INSTALLATION,
  Selector
} from 'views/ProductPage/configurators/installation/lawnmower/addon';
import {
  isLawnMowerConfigurator,
  isVvsConfigurator
} from 'views/ProductPage/configurators/selector';
import {
  VvsAddonProduct,
  VvsAddonSection
} from 'views/ProductPage/configurators/installation/vvs/addon';
import cn from 'classnames';
import { isMobile } from 'components/Shared/responsive';
import { InstallationProps } from 'views/ProductPage/configurators/installation';
import { InstallationContainer } from 'views/ProductPage/configurators/installation/installationContainer';
import { ZipCodeInput } from 'components/Shared/ZipCodeInput';
import { useDispatch } from 'react-redux';
import {
  getFormattedZipCode,
  isValidZipcode
} from 'components/Shared/ZipCodeInput/themes/BGH/validation';
import { GTMInstallationZipCode } from 'store/googleTagManager/actions';

interface OwnProps extends ConfiguratorProps {
  configurator: LawnMowerInstallationConfigurator | VvsInstallationConfigurator;
  showInstallation: boolean;
}

interface MergedProps extends OwnProps, InstallationProps {}

interface OverridableProps extends OwnProps, Partial<InstallationProps> {}

interface OwnState {
  show: boolean;
  modalContent?: ReactComponent;
  validInstallations: string[];
  loadingStatus: LoadingStatus;
}

const initialState: OwnState = {
  show: false,
  modalContent: undefined,
  validInstallations: [],
  loadingStatus: LoadingStatus.Idle
};

type OwnAction =
  | { type: 'toggle'; show: boolean }
  | { type: 'fetchValidInstallationIds' }
  | { type: 'fetchValidInstallationIdsSuccess'; installationIds: string[] }
  | { type: 'fetchValidInstallationIdsError' };

const ownReducer: Reducer<OwnState, OwnAction> = (state, action) => {
  switch (action.type) {
    case 'toggle':
      return {
        ...state,
        show: action.show,
        loadingStatus: LoadingStatus.Idle,
        validInstallations: []
      };
    case 'fetchValidInstallationIds':
      return { ...state, loadingStatus: LoadingStatus.Fetching };
    case 'fetchValidInstallationIdsSuccess':
      return {
        ...state,
        loadingStatus: LoadingStatus.Completed,
        validInstallations: action.installationIds,
        show: true
      };
    case 'fetchValidInstallationIdsError':
      return { ...state, loadingStatus: LoadingStatus.Error, validInstallations: [] };
    default:
      return state;
  }
};

interface AddonSectionProps
  extends Pick<MergedProps, 'configurator' | 'selectAddon' | 'resetAddons'> {
  setModal: () => void;
  addonProducts: IZipFetchInstallationProduct[];
  noAddonsSelected: boolean;
  installationSelected: boolean;
}

const AddonSection = ({ configurator, setModal, addonProducts, ...props }: AddonSectionProps) => {
  if (isLawnMowerConfigurator(configurator)) {
    return (
      <LawnmowerAddonSection
        {...props}
        setModal={setModal}
        addonProducts={addonProducts as LawnMowerAddonProduct[]}
      />
    );
  }
  if (isVvsConfigurator(configurator)) {
    return <VvsAddonSection {...props} addonProducts={addonProducts as VvsInstallationProduct[]} />;
  }
  return NO_ELEMENT;
};

interface InstallationStepperProps
  extends Pick<
    MergedProps,
    'configurator' | 'zipCode' | 'setZipCode' | 'selectAddon' | 'resetAddons'
  > {
  noAddonsSelected: boolean;
  show: boolean;
  installationOptions: ReactNode;
  installationSelected: boolean;
  addonProducts: IZipFetchInstallationProduct[];
  setModal: () => void;
  loadingStatus: LoadingStatus;
}

const InstallationStepper = ({
  configurator,
  noAddonsSelected,
  zipCode,
  setZipCode,
  show,
  installationOptions,
  installationSelected,
  selectAddon,
  resetAddons,
  addonProducts,
  setModal,
  loadingStatus
}: InstallationStepperProps) => {
  const [installation, setInstallation] = useState(INSTALLATION.NONE);

  const displayHelpAddons = installation === INSTALLATION.HELP || isVvsConfigurator(configurator);
  const displayAddonSelector = isLawnMowerConfigurator(configurator);
  const displayDefaultAddons =
    installation === INSTALLATION.NO_HELP || isVvsConfigurator(configurator);

  return (
    <>
      {displayAddonSelector && (
        <Selector onChange={setInstallation} selected={installation} configurator={configurator} />
      )}
      {displayHelpAddons && (
        <>
          <ZipCodeInput
            title={rm('product_polarpump_installation_zipcode_header')}
            description={rm('product_polarpump_body')}
            zipCode={zipCode}
            onChange={setZipCode}
            loadingStatus={loadingStatus}
            inputClassName={styles.zipInput}
            descriptionClassName={styles.description}
          />
          {show ? installationOptions : NO_ELEMENT}
        </>
      )}
      {displayDefaultAddons && (
        <AddonSection
          setModal={setModal}
          addonProducts={addonProducts}
          configurator={configurator}
          noAddonsSelected={noAddonsSelected}
          installationSelected={installationSelected}
          selectAddon={selectAddon}
          resetAddons={resetAddons}
        />
      )}
    </>
  );
};

const InstallationConfigurator = ({
  resetConfigurator,
  configurator,
  selectInstallation,
  selectAddon,
  resetAddons,
  setModal,
  zipCode,
  setZipCode,
  handleError,
  showInstallation,
  isMobile,
  inDrawer
}: MergedProps & ModalProps) => {
  const [state, dispatch] = useReducer(ownReducer, initialState);
  const reduxDispatch = useDispatch();

  const formattedZipCode = getFormattedZipCode(zipCode);

  const installationProducts = useMemo(
    () => getProducts(configurator.installation),
    [configurator.installation]
  );
  const addonProducts = useMemo(() => getProducts(configurator.addons), [configurator.addons]);
  const filteredInstallations = useMemo(
    () => installationProducts.filter(p => state.validInstallations.includes(p.installationId)),
    [installationProducts, state.validInstallations]
  );
  const noAddonsSelected = useMemo(() => addonProducts.every(p => !p.selected), [addonProducts]);
  const installationSelected = useMemo(
    () => filteredInstallations.some(p => p.selected),
    [filteredInstallations]
  );

  const noInstallationsFound =
    state.loadingStatus === LoadingStatus.Completed &&
    isValidZipcode(formattedZipCode) &&
    filteredInstallations.length === 0;

  useEffect(() => {
    if (!isValidZipcode(formattedZipCode)) {
      dispatch({ type: 'toggle', show: false });

      if (installationSelected) {
        resetConfigurator();
      }
    }

    if (installationSelected) {
      resetAddons();
    }
  }, [zipCode, installationSelected, resetConfigurator, resetAddons, formattedZipCode]);

  useEffect(() => {
    const shouldCheckZipCode =
      isValidZipcode(formattedZipCode) && state.loadingStatus === LoadingStatus.Idle;
    if (shouldCheckZipCode) {
      dispatch({ type: 'fetchValidInstallationIds' });
      const installationIds = Array.from(new Set(installationProducts.map(p => p.installationId)));
      postCheckInstallationZipCode({
        zipCode: formattedZipCode,
        installationIds
      })
        .then(res => {
          //If the result contains any installation Ids the zip code is valid for installations
          reduxDispatch(GTMInstallationZipCode(res.installationIds.length > 0));

          dispatch({
            type: 'fetchValidInstallationIdsSuccess',
            installationIds: res.installationIds
          });
        })
        .catch(handleError);
    }
  }, [
    zipCode,
    installationProducts,
    state.loadingStatus,
    handleError,
    reduxDispatch,
    formattedZipCode
  ]);

  const renderInstallation = (p: IInstallationAddonProduct) => {
    const groupName = 'lawnMover';
    if (isLawnMowerProduct(p)) {
      return (
        <LawnmowerAddon
          setModal={setModal}
          descriptionType="modal"
          {...p}
          key={p.productId}
          onChange={selectInstallation}
          name={groupName}
        />
      );
    }

    if (isVvsProduct(p)) {
      return <VvsAddonProduct key={p.productId} {...p} onChange={selectInstallation} />;
    }

    return NO_ELEMENT;
  };

  const installationOptions = !noInstallationsFound ? (
    <div>
      <p className={styles.sectionHeading}>{rm('product_polarpump_installation_type')}</p>
      <fieldset id="installation">{filteredInstallations.map(renderInstallation)}</fieldset>
    </div>
  ) : (
    <p className={styles.description}>{rm('product_polarpump_installation_error_not_in_area')}</p>
  );

  const title = showInstallation
    ? rm('product_polarpump_header')
    : rm('product_polarpump_installation_disabled');
  const mobileOrDrawer = isMobile || inDrawer;

  return (
    <InstallationContainer
      title={title}
      configurator={configurator}
      disabled={!showInstallation}
      initialOpen={showInstallation}
      isMobile={isMobile}
      inDrawer={inDrawer}
    >
      <div
        className={cn(styles.body, {
          [styles.mobile]: mobileOrDrawer
        })}
      >
        <InstallationStepper
          setModal={setModal}
          configurator={configurator}
          addonProducts={addonProducts}
          noAddonsSelected={noAddonsSelected}
          zipCode={zipCode}
          setZipCode={setZipCode}
          show={state.show}
          installationOptions={installationOptions}
          installationSelected={installationSelected}
          selectAddon={selectAddon}
          resetAddons={resetAddons}
          loadingStatus={state.loadingStatus}
        />
      </div>
    </InstallationContainer>
  );
};

interface StateProps {
  inputs: Record<string, string>;
}

export default connect<MergedProps, OverridableProps, StateProps>(
  state => ({
    isMobile: isMobile(state),
    isInstallationSelected: isInstallationSelected(state.currentPage),
    inputs: getConfiguratorVariables(state.currentPage)
  }),
  dispatch => ({
    selectInstallation: (productId: string) => dispatch(selectConfiguratorInstallation(productId)),
    selectAddon: (productId: string) => dispatch(selectConfiguratorAddon(productId)),
    resetAddons: () => dispatch(resetConfiguratorAddons()),
    resetConfigurator: () => dispatch(resetInstallationConfigurator()),
    handleError: (response: IBasicResponse) => handleServerError(dispatch)(response),
    setZipCode: ({ target: { value } }) =>
      dispatch(value ? onSelectValue('zipCode', value) : onDeleteValue('zipCode'))
  }),
  ({ inputs, ...state }, dispatch, props) => ({
    ...state,
    ...dispatch,
    ...props,
    zipCode: props.zipCode || inputs.zipCode || ''
  })
)(InstallationConfigurator);
